'use client';

import { Language } from '@/lib/translations';

interface EmptySearchResultsProps {
  language: Language;
  searchQuery?: string;
  type: 'blog' | 'help' | 'faq' | 'features' | 'changelog';
  customMessage?: string;
}

export default function EmptySearchResults({
  language,
  searchQuery,
  type,
  customMessage,
}: EmptySearchResultsProps) {
  // Define icons and colors for each type
  const typeConfig = {
    blog: {
      icon: '📝',
      color: 'var(--color-foreground)',
      noResultsText: {
        en: 'No blog posts found',
        cn: '未找到部落格文章',
      },
      searchNoResultsText: {
        en: 'No posts match your search',
        cn: '沒有文章符合您的搜索',
      },
      suggestionText: {
        en: 'Try adjusting your search or browse by category',
        cn: '嘗試調整您的搜尋或按分類瀏覽',
      },
    },
    help: {
      icon: '🔍',
      color: 'var(--color-foreground)',
      noResultsText: {
        en: 'No help articles found',
        cn: '未找到幫助文章',
      },
      searchNoResultsText: {
        en: 'No articles match your search',
        cn: '沒有文章符合您的搜索',
      },
      suggestionText: {
        en: 'Try adjusting your search or browse by category',
        cn: '嘗試調整您的搜尋或按分類瀏覽',
      },
    },
    faq: {
      icon: '❓',
      color: 'var(--color-foreground)',
      noResultsText: {
        en: 'No FAQ items found',
        cn: '未找到常見問題',
      },
      searchNoResultsText: {
        en: 'No FAQs match your search',
        cn: '沒有常見問題符合您的搜索',
      },
      suggestionText: {
        en: 'Try adjusting your search or contact our support team',
        cn: '嘗試調整您的搜尋或聯繫我們的支援團隊',
      },
    },
    features: {
      icon: '⚡',
      color: 'var(--color-foreground)',
      noResultsText: {
        en: 'No features found',
        cn: '找不到功能',
      },
      searchNoResultsText: {
        en: 'No features match your search',
        cn: '沒有功能符合您的搜索',
      },
      suggestionText: {
        en: 'Try adjusting your search terms or filters',
        cn: '嘗試調整您的搜索條件或篩選器',
      },
    },
    changelog: {
      icon: '📜',
      color: 'var(--color-foreground)',
      noResultsText: {
        en: 'No changelog entries found',
        cn: '找不到更新紀錄',
      },
      searchNoResultsText: {
        en: 'No updates match your search',
        cn: '沒有更新紀錄符合您的搜索',
      },
      suggestionText: {
        en: 'Try a different search or clear category filters',
        cn: '請嘗試不同的搜尋或清除分類篩選',
      },
    },
  };

  const config = typeConfig[type];
  const isSearching = searchQuery && searchQuery.trim().length > 0;

  const title = isSearching
    ? language === 'en'
      ? config.searchNoResultsText.en
      : config.searchNoResultsText.cn
    : language === 'en'
      ? config.noResultsText.en
      : config.noResultsText.cn;

  const suggestion =
    customMessage ||
    (language === 'en' ? config.suggestionText.en : config.suggestionText.cn);

  return (
    <div className="text-center py-12">
      <div className="text-6xl mb-4">{config.icon}</div>
      <h3
        className="text-xl font-semibold mb-2"
        style={{ color: config.color }}
      >
        {title}
      </h3>
      <p className="text-gray-600 dark:text-gray-400 mb-4">{suggestion}</p>
      {isSearching && (
        <p className="text-sm text-gray-500 dark:text-gray-500">
          {language === 'en'
            ? `Searched for: "${searchQuery}"`
            : `搜索條件：「${searchQuery}」`}
        </p>
      )}
    </div>
  );
}
